/**
 * Structured feeding schedule + feeding log (migration 0101) — client types,
 * closed vocab, presentation helpers and the staff API surface.
 *
 * Mirrors `apps/api/src/feeding/plan.ts`. The customer portal reuses the
 * types + helpers and talks to `/api/customer/pets/:id/feeding-plan` via
 * `customerApi`; the staff functions here go through the same cookie-auth
 * `fetch` as `lib/api.ts`.
 */

export const FEEDING_SLOTS = ['morning', 'midday', 'evening', 'bedtime', 'custom'] as const;
export type FeedingSlot = (typeof FEEDING_SLOTS)[number];

export const FEEDING_UNITS = ['cup', 'g', 'ml', 'scoop', 'pouch', 'can', 'other'] as const;
export type FeedingUnit = (typeof FEEDING_UNITS)[number];

export const FEEDING_OUTCOMES = ['eaten', 'partial', 'refused', 'skipped'] as const;
export type FeedingOutcome = (typeof FEEDING_OUTCOMES)[number];

export const FEEDING_SLOT_LABELS: Record<FeedingSlot, string> = {
  morning: 'Morning',
  midday: 'Midday',
  evening: 'Evening',
  bedtime: 'Bedtime',
  custom: 'Custom',
};

export const FEEDING_UNIT_LABELS: Record<FeedingUnit, string> = {
  cup: 'cup',
  g: 'g',
  ml: 'ml',
  scoop: 'scoop',
  pouch: 'pouch',
  can: 'can',
  other: 'other',
};

export const FEEDING_OUTCOME_LABELS: Record<FeedingOutcome, string> = {
  eaten: 'Eaten',
  partial: 'Partial',
  refused: 'Refused',
  skipped: 'Skipped',
};

export interface FeedingPlanItem {
  slot: FeedingSlot;
  time?: string;
  food: string;
  quantity: string;
  unit: FeedingUnit;
  supplements?: string;
  notes?: string;
}

/** Editor draft — every field present so inputs stay controlled. */
export interface FeedingPlanDraft {
  items: FeedingPlanItem[];
  ownerSupplied: boolean;
  allergies: string;
  redFlags: string;
}

export interface FeedingPlanView {
  id: string;
  petId: string;
  bookingId: string | null;
  items: FeedingPlanItem[];
  ownerSupplied: boolean;
  allergies: string | null;
  redFlags: string | null;
  updatedAt: string;
}

export type FeedingPlanSource = 'stay' | 'pet' | 'legacy_text' | 'none';

export interface FeedingLogEntry {
  id: string;
  bookingId: string;
  petId: string;
  slot: FeedingSlot | string;
  slotTime: string | null;
  scheduledFor: string;
  givenAt: string;
  givenByMembershipId: string | null;
  outcome: FeedingOutcome | string;
  notes: string | null;
}

export interface PetFeedingPlanResponse {
  plan: FeedingPlanView | null;
  legacyText: string | null;
}

export interface BookingFeedingPlanResponse {
  petId: string;
  bookingType: string;
  feedingApplies: boolean;
  plan: FeedingPlanView | null;
  source: FeedingPlanSource;
  text: string;
  legacyText: string | null;
  override: FeedingPlanView | null;
  petDefault: FeedingPlanView | null;
}

export interface BookingFeedingLogResponse {
  petId: string;
  today: string;
  date: string | null;
  feedingApplies: boolean;
  slots: FeedingPlanItem[];
  source: FeedingPlanSource;
  text: string;
  entries: FeedingLogEntry[];
}

export const EMPTY_FEEDING_DRAFT: FeedingPlanDraft = {
  items: [],
  ownerSupplied: false,
  allergies: '',
  redFlags: '',
};

export function newFeedingItem(slot: FeedingSlot = 'morning'): FeedingPlanItem {
  return { slot, food: '', quantity: '1', unit: 'cup' };
}

export function draftFromPlan(plan: FeedingPlanView | null | undefined): FeedingPlanDraft {
  if (!plan) return { ...EMPTY_FEEDING_DRAFT, items: [] };
  return {
    items: plan.items.map((it) => ({ ...it })),
    ownerSupplied: plan.ownerSupplied,
    allergies: plan.allergies ?? '',
    redFlags: plan.redFlags ?? '',
  };
}

/** Body shape for the PUT routes. Empty strings become null. */
export function draftToBody(draft: FeedingPlanDraft): Record<string, unknown> {
  return {
    items: draft.items.map((it) => {
      const out: Record<string, unknown> = {
        slot: it.slot,
        food: it.food.trim(),
        quantity: String(it.quantity).trim(),
        unit: it.unit,
      };
      if (it.time) out.time = it.time;
      if (it.supplements?.trim()) out.supplements = it.supplements.trim();
      if (it.notes?.trim()) out.notes = it.notes.trim();
      return out;
    }),
    ownerSupplied: draft.ownerSupplied,
    allergies: draft.allergies.trim() || null,
    redFlags: draft.redFlags.trim() || null,
  };
}

/** True when the draft carries anything worth saving. */
export function draftHasContent(draft: FeedingPlanDraft): boolean {
  return (
    draft.items.some((it) => it.food.trim().length > 0) ||
    draft.ownerSupplied ||
    draft.allergies.trim().length > 0 ||
    draft.redFlags.trim().length > 0
  );
}

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Client-side mirror of the API validator — returns human issues. */
export function validateFeedingDraft(draft: FeedingPlanDraft): string[] {
  const issues: string[] = [];
  draft.items.forEach((it, i) => {
    const label = `Meal ${i + 1}`;
    if (!it.food.trim()) issues.push(`${label}: food is required`);
    if (!String(it.quantity).trim()) issues.push(`${label}: quantity is required`);
    if (it.time && !HHMM_RE.test(it.time)) issues.push(`${label}: time must be HH:MM`);
    if (it.slot === 'custom' && !it.time) issues.push(`${label}: custom slots need a time`);
  });
  return issues;
}

const SLOT_ORDER: Record<FeedingSlot, number> = {
  morning: 0,
  midday: 1,
  evening: 2,
  bedtime: 3,
  custom: 4,
};

export function sortFeedingItems(items: readonly FeedingPlanItem[]): FeedingPlanItem[] {
  return [...items].sort((a, b) => {
    const d = SLOT_ORDER[a.slot] - SLOT_ORDER[b.slot];
    if (d !== 0) return d;
    return (a.time ?? '').localeCompare(b.time ?? '');
  });
}

export function feedingSlotLabel(item: { slot: string; time?: string | null }): string {
  const base = FEEDING_SLOT_LABELS[item.slot as FeedingSlot] ?? item.slot;
  return item.time ? `${base} ${item.time}` : base;
}

export function formatFeedingItem(item: FeedingPlanItem): string {
  const unit = item.unit === 'other' ? '' : ` ${FEEDING_UNIT_LABELS[item.unit]}`;
  let s = `${item.quantity}${unit} ${item.food}`.trim();
  if (item.supplements) s += ` + ${item.supplements}`;
  if (item.notes) s += ` (${item.notes})`;
  return s;
}

/** Key that pairs a plan slot with its log entry (slot + custom time). */
export function feedingSlotKey(item: {
  slot: string;
  time?: string | null;
  slotTime?: string | null;
}): string {
  const time = item.time ?? item.slotTime ?? '';
  return `${item.slot}:${time}`;
}

// ---------------------------------------------------------------------------
// Staff API
// ---------------------------------------------------------------------------

async function staffFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      issues?: string[];
    };
    const detail = payload.issues?.length ? `: ${payload.issues.join('; ')}` : '';
    throw new Error(`${payload.error ?? `http_${res.status}`}${detail}`);
  }
  return res.json() as Promise<T>;
}

export const getPetFeedingPlan = (petId: string) =>
  staffFetch<PetFeedingPlanResponse>('GET', `/pets/${petId}/feeding-plan`);

export const putPetFeedingPlan = (petId: string, draft: FeedingPlanDraft) =>
  staffFetch<{ plan: FeedingPlanView }>('PUT', `/pets/${petId}/feeding-plan`, draftToBody(draft));

export const getBookingFeedingPlan = (bookingId: string) =>
  staffFetch<BookingFeedingPlanResponse>('GET', `/bookings/${bookingId}/feeding-plan`);

export const putBookingFeedingPlan = (bookingId: string, draft: FeedingPlanDraft) =>
  staffFetch<{ plan: FeedingPlanView }>(
    'PUT',
    `/bookings/${bookingId}/feeding-plan`,
    draftToBody(draft),
  );

export const deleteBookingFeedingPlan = (bookingId: string) =>
  staffFetch<{ ok: true; deleted: boolean }>('DELETE', `/bookings/${bookingId}/feeding-plan`);

export const getBookingFeedingLog = (bookingId: string, date?: string) =>
  staffFetch<BookingFeedingLogResponse>(
    'GET',
    `/bookings/${bookingId}/feeding-log${date ? `?date=${encodeURIComponent(date)}` : ''}`,
  );

export const recordFeedingOutcome = (
  bookingId: string,
  input: {
    slot: FeedingSlot;
    slotTime?: string | null;
    scheduledFor?: string;
    outcome: FeedingOutcome;
    notes?: string | null;
  },
) =>
  staffFetch<{ entry: FeedingLogEntry; created: boolean }>(
    'POST',
    `/bookings/${bookingId}/feeding-log`,
    input,
  );
